import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getLeadDetails, getSavedClassList, sendDocChecklist } from "../../api/discussion";
import { getWorkflowStatus } from "api/applications";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import { DOCUMENTS_PREVIEW_TEXT } from "constants/messagePreviews";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";

function formatClassesFromApi(classes: { classNumber: number; goodsServices: string[] }[]): string {
  if (!classes || classes.length === 0) return "";
  return classes.map((c) => `Class ${c.classNumber}`).join(", ");
}

function FormField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label
        className="text-xs font-normal leading-5 text-[#222222] flex items-center"
        style={{ fontFamily: "Inter, sans-serif", fontFeatureSettings: "'ss01' on, 'cv01' on" }}
      >
        {label}
      </label>
      <div
        className="box-border flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-[10px] py-2 h-9 w-full focus-within:ring-1 focus-within:ring-[#222]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent text-xs font-normal leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none"
          style={{ fontFamily: "Inter, sans-serif", fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
      </div>
    </div>
  );
}

type ChecklistDocumentsToClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function ChecklistDocumentsToClientView({
  leadId,
  onSuccess,
}: ChecklistDocumentsToClientViewProps) {
  const [markName, setMarkName] = useState("");
  const [classes, setClasses] = useState("");
  const [loading, setLoading] = useState(!!leadId);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [resending, setResending] = useState(false);
  const [showResendConfirmModal, setShowResendConfirmModal] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([getLeadDetails(leadId), getSavedClassList(leadId)])
      .then(([leadRes, savedClassList]) => {
        if (cancelled) return;
        const { lead } = leadRes;
        if (lead.markName) setMarkName(lead.markName);
        if (savedClassList?.classes && savedClassList.classes.length > 0) {
          setClasses(formatClassesFromApi(savedClassList.classes));
        } else if (savedClassList?.markName && !lead.markName) {
          setMarkName(savedClassList.markName);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load lead details");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getWorkflowStatus(leadId);
        const step = status.steps.find(
          (s) => s.slug === "S2_01_DOC_CHECKLIST_TO_CLIENT"
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
        }
      } catch {
        if (!cancelled) setHasSentWhatsapp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading lead details...</p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Row: Mark Name + Classes - prefilled from API when leadId present */}
      <div
        className="flex flex-row items-center gap-5 w-full"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <FormField
          label="Mark Name"
          value={markName}
          onChange={setMarkName}
          placeholder="ABC Company"
        />
        <FormField
          label="Classes"
          value={classes}
          onChange={setClasses}
          placeholder="Class 18"
        />
      </div>

      {/* Document checklist preview - same content sent to the client via WhatsApp */}
      <div className="flex flex-col gap-2 w-full">
        <label
          className="text-xs font-normal leading-5 text-[#222222] flex items-center"
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        >
          Document Checklist
        </label>
        <div className="flex flex-col gap-2.5 rounded-[10px] bg-[#f9f9f9] p-4">
          <div className="flex items-start rounded-lg bg-white px-3.5 py-2.5">
            <pre
              className="min-w-0 flex-1 whitespace-pre-wrap break-words font-sans text-sm font-normal leading-5 text-[#222]"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              {renderWhatsAppFormatting(DOCUMENTS_PREVIEW_TEXT)}
            </pre>
          </div>
        </div>
      </div>

      {hasSentWhatsapp && leadId && (
        <div className="flex flex-row items-center gap-3 w-full">
          <button
            type="button"
            disabled={resending}
            onClick={() => {
              if (!leadId || resending) return;
              setShowResendConfirmModal(true);
            }}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}

      <ConfirmMessageModal
        open={showResendConfirmModal}
        onClose={() => setShowResendConfirmModal(false)}
        title="Resend document checklist"
        previewContent={DOCUMENTS_PREVIEW_TEXT}
        disableConfirm={resending}
        onConfirm={() => {
          setShowResendConfirmModal(false);
          if (!leadId) return;
          setResending(true);
          void (async () => {
            try {
              await sendDocChecklist(leadId);
              toast.success("Document checklist resent to client via WhatsApp.");
              onSuccess?.(leadId);
            } catch (err) {
              toast.error(
                err instanceof Error
                  ? err.message
                  : "Failed to resend document checklist"
              );
            } finally {
              setResending(false);
            }
          })();
        }}
      />
    </div>
  );
}
